export const calcCompetitorLineSubtotal = (quantity, unitPrice) => {
  const qty = Number(quantity || 0);
  const price = Number(unitPrice || 0);
  if (!Number.isFinite(qty) || !Number.isFinite(price)) return 0;
  return qty * price;
};

export const calcOurLineSubtotal = (item = {}) => {
  const lineTotal = Number(item.line_total);
  if (Number.isFinite(lineTotal)) return lineTotal;
  return calcCompetitorLineSubtotal(item.quantity, item.unit_price);
};

export const calcOfferTotals = (quotationItems = [], lineMap = {}) => {
  let ourTotal = 0;
  let competitorTotal = 0;
  (quotationItems || []).forEach((item) => {
    ourTotal += calcOurLineSubtotal(item);
    const itemId = Number(item.id);
    const row = lineMap[itemId] || {};
    const unitPrice = Number(row.unitPrice || 0);
    if (unitPrice > 0) {
      competitorTotal += calcCompetitorLineSubtotal(item.quantity, unitPrice);
    }
  });
  return { ourTotal, competitorTotal };
};

export const calcPriceVariationPct = (ourUnitPrice, competitorUnitPrice) => {
  const ours = Number(ourUnitPrice || 0);
  const theirs = Number(competitorUnitPrice || 0);
  if (!Number.isFinite(ours) || ours <= 0 || !Number.isFinite(theirs) || theirs <= 0) return null;
  return ((theirs - ours) / ours) * 100;
};

export const calcAggregateVariationPct = (quotationItems = [], lineMap = {}) => {
  const { competitorTotal } = calcOfferTotals(quotationItems, lineMap);
  const comparableOurTotal = (quotationItems || []).reduce((sum, item) => {
    const itemId = Number(item.id);
    const row = lineMap[itemId] || {};
    const unitPrice = Number(row.unitPrice || 0);
    if (!(unitPrice > 0)) return sum;
    return sum + calcOurLineSubtotal(item);
  }, 0);
  if (comparableOurTotal <= 0) return null;
  return ((competitorTotal - comparableOurTotal) / comparableOurTotal) * 100;
};

export const formatVariationPct = (value) => {
  if (value == null || !Number.isFinite(value)) return '—';
  const rounded = Math.round(value * 10) / 10;
  const sign = rounded > 0 ? '+' : '';
  return `${sign}${rounded.toLocaleString('es-CL', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
};

export const parseLossCompetitor = (raw) => {
  if (!raw) {
    return { competitor_id: null, competitor_name: '', competitor_amount: 0, line_items: [] };
  }
  let payload = raw;
  if (typeof raw === 'string') {
    try {
      payload = JSON.parse(raw);
    } catch {
      return { competitor_id: null, competitor_name: '', competitor_amount: 0, line_items: [] };
    }
  }
  if (!payload || typeof payload !== 'object') {
    return { competitor_id: null, competitor_name: '', competitor_amount: 0, line_items: [] };
  }
  const lineItems = Array.isArray(payload.line_items)
    ? payload.line_items.map((entry) => ({
        quotation_item_id: Number(entry?.quotation_item_id || entry?.item_id || 0) || null,
        competitor_product: String(entry?.competitor_product || entry?.product || '').trim(),
        competitor_unit_price: Number(entry?.competitor_unit_price || entry?.unit_price || 0) || 0,
      }))
    : [];
  const competitorId = Number(payload.competitor_id || 0);
  return {
    competitor_id: Number.isFinite(competitorId) && competitorId > 0 ? competitorId : null,
    competitor_name: String(payload.competitor_name || '').trim(),
    competitor_amount: Number(payload.competitor_amount || 0) || 0,
    line_items: lineItems,
  };
};

export const buildLossCompetitorLineMap = (lineItems = []) => {
  const map = {};
  (lineItems || []).forEach((entry) => {
    const itemId = Number(entry?.quotation_item_id || 0);
    if (!itemId) return;
    map[itemId] = {
      product: String(entry?.competitor_product || '').trim(),
      unitPrice: entry?.competitor_unit_price == null || entry?.competitor_unit_price === ''
        ? ''
        : String(entry.competitor_unit_price),
    };
  });
  return map;
};

export const resolveCompetitorSelection = (parsed, competitors = []) => {
  const list = Array.isArray(competitors) ? competitors : [];
  const competitorId = Number(parsed?.competitor_id || 0);
  if (competitorId > 0) {
    const byId = list.find((entry) => Number(entry.id) === competitorId);
    if (byId) {
      return { selection: String(competitorId), newName: '', showNew: false };
    }
  }
  const competitorName = String(parsed?.competitor_name || '').trim();
  if (competitorName) {
    const byName = list.find(
      (entry) => String(entry.name || '').trim().toLowerCase() === competitorName.toLowerCase()
    );
    if (byName) {
      return { selection: String(byName.id), newName: '', showNew: false };
    }
    return { selection: '', newName: competitorName, showNew: true };
  }
  return { selection: '', newName: '', showNew: false };
};

export const buildLossCompetitorPayload = ({
  competitorId = 0,
  competitorName = '',
  competitorAmount = '',
  lineMap = {},
  quotationItems = [],
} = {}) => {
  const line_items = (quotationItems || [])
    .map((item) => {
      const itemId = Number(item.id);
      const row = lineMap[itemId] || {};
      const product = String(row.product || '').trim();
      const unitPrice = Number(row.unitPrice || 0);
      if (!product && !(unitPrice > 0)) return null;
      return {
        quotation_item_id: itemId,
        competitor_product: product,
        competitor_unit_price: unitPrice > 0 ? unitPrice : 0,
      };
    })
    .filter(Boolean);

  const amount = Number(competitorAmount || 0);
  const id = Number(competitorId || 0);
  return {
    competitor_id: Number.isFinite(id) && id > 0 ? id : null,
    competitor_name: String(competitorName || '').trim(),
    competitor_amount: Number.isFinite(amount) && amount > 0 ? amount : 0,
    line_items,
  };
};

export const lossCompetitorHasData = (data) => {
  const parsed = parseLossCompetitor(data);
  return Boolean(
    parsed.competitor_id
    || parsed.competitor_name
    || Number(parsed.competitor_amount || 0) > 0
    || (parsed.line_items || []).some(
      (row) => row.competitor_product || Number(row.competitor_unit_price || 0) > 0
    )
  );
};

export const formatLossCompetitorMoney = (value, currency = 'CLP') =>
  new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(Number(value || 0));
